import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Avatar,
  Grid,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import { motion } from 'framer-motion';
import { glassStyle, animationVariants } from '../theme/luxuryTheme';

interface BrandComparisonProps {
  selectedBrand?: string;
}

const brandColors: Record<string, string> = {
  'Savvy Sliders': '#af2326',
  'Happy\'s Pizza': '#ff6b35',
  'BurgerFi': '#1976d2',
};

const brandIcons: Record<string, string> = {
  'Savvy Sliders': '🍔',
  'Happy\'s Pizza': '🍕',
  'BurgerFi': '🍔',
};

const BrandComparison: React.FC<BrandComparisonProps> = ({ selectedBrand }) => {
  const theme = useTheme();
  const [comparisonView, setComparisonView] = useState<'revenue' | 'orders' | 'growth'>('revenue');

  // Mock data for brand comparison
  const brandData = [
    { brand: 'Savvy Sliders', revenue: 125000, orders: 2500, growth: 12.5, avgOrder: 50 },
    { brand: 'Happy\'s Pizza', revenue: 98000, orders: 1800, growth: 15.2, avgOrder: 54.44 },
    { brand: 'BurgerFi', revenue: 87000, orders: 1450, growth: 8.3, avgOrder: 60 },
  ];

  const monthlyTrend = [
    { month: 'Jan', 'Savvy Sliders': 110000, 'Happy\'s Pizza': 85000, 'BurgerFi': 75000 },
    { month: 'Feb', 'Savvy Sliders': 115000, 'Happy\'s Pizza': 90000, 'BurgerFi': 78000 },
    { month: 'Mar', 'Savvy Sliders': 120000, 'Happy\'s Pizza': 94000, 'BurgerFi': 82000 },
    { month: 'Apr', 'Savvy Sliders': 125000, 'Happy\'s Pizza': 98000, 'BurgerFi': 87000 },
  ];

  const performanceMetrics = [
    { metric: 'Revenue', 'Savvy Sliders': 95, 'Happy\'s Pizza': 75, 'BurgerFi': 65 },
    { metric: 'Orders', 'Savvy Sliders': 90, 'Happy\'s Pizza': 78, 'BurgerFi': 60 },
    { metric: 'Growth', 'Savvy Sliders': 80, 'Happy\'s Pizza': 95, 'BurgerFi': 65 },
    { metric: 'Avg Order', 'Savvy Sliders': 75, 'Happy\'s Pizza': 80, 'BurgerFi': 90 },
    { metric: 'Efficiency', 'Savvy Sliders': 85, 'Happy\'s Pizza': 82, 'BurgerFi': 78 },
  ];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            ...glassStyle,
            p: 2,
            borderRadius: '12px',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: entry.color,
                }}
              />
              <Typography variant="body2">
                {entry.name}: ${entry.value.toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <motion.div {...animationVariants.fadeIn}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Brand Performance Comparison
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Compare sales performance across all brands
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={comparisonView}
            exclusive
            onChange={(_, newView) => newView && setComparisonView(newView)}
            size="small"
          >
            <ToggleButton value="revenue">Revenue</ToggleButton>
            <ToggleButton value="orders">Orders</ToggleButton>
            <ToggleButton value="growth">Growth</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </motion.div>

      <Grid container spacing={3}>
        {/* Brand Summary Cards */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {brandData.map((brand, index) => (
              <Grid item xs={12} sm={6} md={3} key={brand.brand}>
                <motion.div
                  {...animationVariants.scale}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      ...glassStyle,
                      p: 2,
                      border: selectedBrand === brand.brand ? `2px solid ${brandColors[brand.brand]}` : 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 32px ${brandColors[brand.brand]}40`,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: brandColors[brand.brand],
                          fontSize: '1.5rem',
                        }}
                      >
                        {brandIcons[brand.brand]}
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {brand.brand}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Revenue
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        ${(brand.revenue / 1000).toFixed(0)}K
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Orders
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {brand.orders.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Growth
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.success.main,
                          }}
                        >
                          +{brand.growth}%
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Comparison Chart */}
        <Grid item xs={12} lg={8}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.4 }}>
            <Card sx={{ ...glassStyle, p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {comparisonView === 'revenue' && 'Revenue Comparison'}
                {comparisonView === 'orders' && 'Order Volume Comparison'}
                {comparisonView === 'growth' && 'Growth Rate Comparison'}
              </Typography>
              
              <ResponsiveContainer width="100%" height="90%">
                {comparisonView === 'growth' ? (
                  <LineChart data={monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                    <XAxis dataKey="month" stroke="#A0AEC0" />
                    <YAxis stroke="#A0AEC0" />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {Object.keys(brandColors).map((brand) => (
                      <Line
                        key={brand}
                        type="monotone"
                        dataKey={brand}
                        stroke={brandColors[brand]}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    ))}
                  </LineChart>
                ) : (
                  <BarChart data={brandData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                    <XAxis dataKey="brand" stroke="#A0AEC0" />
                    <YAxis stroke="#A0AEC0" />
                    <Tooltip />
                    <Bar
                      dataKey={comparisonView}
                      radius={[8, 8, 0, 0]}
                      fill="#af2326"
                    >
                      {brandData.map((entry, index) => (
                        <Bar key={`cell-${index}`} fill={brandColors[entry.brand]} />
                      ))}
                    </Bar>
                  </BarChart>
                )}
              </ResponsiveContainer>
            </Card>
          </motion.div>
        </Grid>

        {/* Performance Radar */}
        <Grid item xs={12} lg={4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.5 }}>
            <Card sx={{ ...glassStyle, p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Performance Metrics
              </Typography>
              
              <ResponsiveContainer width="100%" height="90%">
                <RadarChart data={performanceMetrics}>
                  <PolarGrid stroke="rgba(255, 255, 255, 0.1)" />
                  <PolarAngleAxis dataKey="metric" stroke="#A0AEC0" />
                  <PolarRadiusAxis stroke="#A0AEC0" domain={[0, 100]} />
                  {Object.keys(brandColors).map((brand) => (
                    <Radar
                      key={brand}
                      name={brand}
                      dataKey={brand}
                      stroke={brandColors[brand]}
                      fill={brandColors[brand]}
                      fillOpacity={0.3}
                    />
                  ))}
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BrandComparison;